import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import baseApi from "../../api/baseApi";
import { Slide, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initialState = {
  ticket: {},
  ticketLoading: true,
  ticketRefresh: true,

  tickets: [],
  ticketsLoading: true,
  ticketsRefresh: true,

  createTicketLoading: false,
  deleteTicketLoading: false,
  replyTicketLoading: false,
  closeTicketLoading: false,
};

export const fetchTickets = createAsyncThunk(
  "ticket/fetchTickets",
  async () => {
    try {
      const response = await baseApi.get(`/SupportRequest`);
      return response.data.Value;
    } catch (error) {
      throw new Error(
        error.response?.data?.Error?.Description || error.response?.status
      );
    }
  }
);

export const fetchTicket = createAsyncThunk(
  "ticket/fetchTicket",
  async (id) => {
    if (!id) throw new Error("Id bulunamadı!");

    try {
      const response = await baseApi.get(`/SupportRequest/${id}`);

      return response.data.Value;
    } catch (error) {
      toast.error("Hata: " + error.response.data.Error.Description, {
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      throw new Error(
        error.response?.data?.Error?.Description || error.response?.status
      );
    }
  }
);

export const createTicket = createAsyncThunk(
  "ticket/createTicket",
  async (values) => {
    if (!values) throw new Error("Veri bulunamadı!");

    const data = {
      title: values.title,
      category: values.category,
      message: values.message,
    };

    const loadingToast = toast.loading("Kaydediliyor...", {
      position: "top-center",
      transition: Slide,
    });

    try {
      const response = await baseApi.post(`/SupportRequest`, data);
      toast.update(loadingToast, {
        render: "Destek talebi oluşturuldu.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });
      return response.data.Value;
    } catch (error) {
      toast.update(loadingToast, {
        render: "Hata: " + error.response.data.Error?.Description || "Hata!",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });
      throw new Error(error.response?.data?.Error?.Description);
    }
  }
);

export const deleteTicket = createAsyncThunk(
  "ticket/deleteTicket",
  async (id) => {
    if (!id) throw new Error("Id bulunamadı!");

    const loadingToast = toast.loading("Siliniyor...", {
      position: "top-center",
      transition: Slide,
    });

    try {
      const response = await baseApi.delete(`/SupportRequest/${id}`);
      toast.update(loadingToast, {
        render: "Destek talebi silindi.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });
      return response.data.Value;
    } catch (error) {
      toast.update(loadingToast, {
        render: "Hata: " + error.response.data.Error?.Description || "Hata!",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });
      throw new Error(error.response?.data?.Error?.Description);
    }
  }
);

export const replyTicket = createAsyncThunk(
  "ticket/replyTicket",
  async (values) => {
    if (!values) throw new Error("Veri bulunamadı!");

    const data = {
      supportRequestId: values.supportRequestId,
      message: values.message,
    };

    try {
      const response = await baseApi.post(`/SupportRequest/Reply`, data);
      return response.data.Value;
    } catch (error) {
      throw new Error(error.response?.data?.Error?.Description);
    }
  }
);

export const closeTicket = createAsyncThunk(
  "ticket/closeTicket",
  async (id) => {
    if (!id) throw new Error("Id bulunamadı!");

    const loadingToast = toast.loading("İşleniyor...", {
      position: "top-center",
      transition: Slide,
    });

    try {
      const response = await baseApi.put(`/SupportRequest/Close/${id}`);
      toast.update(loadingToast, {
        render: "Destek talebi kapatıldı.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });
      return response.data.Value;
    } catch (error) {
      toast.update(loadingToast, {
        render: "Hata: " + error.response.data.Error?.Description || "Hata!",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });
      throw new Error(error.response?.data?.Error?.Description);
    }
  }
);

export const ticketSlice = createSlice({
  name: "ticket",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch Tickets
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.tickets = [];
        state.ticketsLoading = true;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.tickets = action.payload;
        state.ticketsLoading = false;
      })
      .addCase(fetchTickets.rejected, (state) => {
        state.tickets = [];
        state.ticketsLoading = false;
      });

    // Fetch Ticket Detail
    builder
      .addCase(fetchTicket.pending, (state) => {
        state.ticket = {};
        state.ticketLoading = true;
      })
      .addCase(fetchTicket.fulfilled, (state, action) => {
        state.ticket = action.payload;
        state.ticketLoading = false;
      })
      .addCase(fetchTicket.rejected, (state) => {
        state.ticket = {};
        state.ticketLoading = false;
      });

    // Create Ticket
    builder
      .addCase(createTicket.pending, (state) => {
        state.createTicketLoading = true;
      })
      .addCase(createTicket.fulfilled, (state) => {
        state.createTicketLoading = false;
        state.ticketsRefresh = !state.ticketsRefresh;
      })
      .addCase(createTicket.rejected, (state) => {
        state.createTicketLoading = false;
      });

    // Delete Ticket
    builder
      .addCase(deleteTicket.pending, (state) => {
        state.deleteTicketLoading = true;
      })
      .addCase(deleteTicket.fulfilled, (state) => {
        state.deleteTicketLoading = false;
        state.ticketsRefresh = !state.ticketsRefresh;
      })
      .addCase(deleteTicket.rejected, (state) => {
        state.deleteTicketLoading = false;
      });

    // Reply Ticket
    builder
      .addCase(replyTicket.pending, (state) => {
        state.replyTicketLoading = true;
      })
      .addCase(replyTicket.fulfilled, (state) => {
        state.replyTicketLoading = false;
        // Listeyi ve detayı yenile
        state.ticketsRefresh = !state.ticketsRefresh;
        state.ticketRefresh = !state.ticketRefresh;
      })
      .addCase(replyTicket.rejected, (state) => {
        state.replyTicketLoading = false;
      });

    // Close Ticket
    builder
      .addCase(closeTicket.pending, (state) => {
        state.closeTicketLoading = true;
      })
      .addCase(closeTicket.fulfilled, (state) => {
        state.closeTicketLoading = false;
        state.ticketsRefresh = !state.ticketsRefresh;
        state.ticketRefresh = !state.ticketRefresh;
      })
      .addCase(closeTicket.rejected, (state) => {
        state.closeTicketLoading = false;
      });
  },
});

export default ticketSlice.reducer;
